import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { checkKmlOverlap } from "src/utils/api/kml";
import { fileUpload } from "src/utils/api/fileupload";
import MapComponent from "src/components/common/KML";

interface KmlOverlapCheckerProps {
  onUploadSuccess: (id: string, url: string) => void;
  existingUpload?: { id: string; url: string } | null;
  disabled?: boolean;
}

type Status = "idle" | "checking" | "uploading" | "success" | "error";

const KmlOverlapChecker = ({
  onUploadSuccess,
  existingUpload,
  disabled = false,
}: KmlOverlapCheckerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [status, setStatus] = useState<Status>(
    existingUpload?.id ? "success" : "idle"
  );
  const [filename, setFilename] = useState(
    existingUpload?.id ? "KML file (previously uploaded)" : ""
  );
  const [kmlUrl, setKmlUrl] = useState<string>(existingUpload?.url ?? "");
  const [overlapOpen, setOverlapOpen] = useState(false);
  const [pariveshWarning, setPariveshWarning] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setFilename(file.name);
    setErrorMsg(null);
    setPariveshWarning(false);

    // Step 1: overlap check
    setStatus("checking");
    try {
      const overlapRes = await checkKmlOverlap(file);
      if (overlapRes.data.overlaps) {
        setOverlapOpen(true);
        setStatus("idle");
        setFilename("");
        setKmlUrl("");
        return;
      }
    } catch {
      // Parivesh portal unreachable — continue with warning
      setPariveshWarning(true);
    }

    // Step 2: upload to S3
    setStatus("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fileUpload(fd as any);
      const uploaded = res?.data?.data?.uploadedFile;
      if (!uploaded?.key) throw new Error("Upload failed");
      setStatus("success");
      setFilename(file.name);
      setKmlUrl(uploaded.fileUrl);
      onUploadSuccess(uploaded.key, uploaded.fileUrl);
    } catch {
      setStatus("error");
      setErrorMsg("Failed to upload KML file. Please try again.");
      setFilename("");
    }
  };

  const isLoading = status === "checking" || status === "uploading";
  const statusLabel =
    status === "checking" ? "Checking overlap…" : "Uploading to S3…";

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
        GPS Boundary (KML file) <span style={{ color: "red" }}>*</span>
      </Typography>
      <Typography
        variant="caption"
        sx={{ mb: 1, display: "block", color: "text.secondary" }}
      >
        Please ensure the KML file contains a closed polygon of the land parcel
      </Typography>

      <Box
        onClick={() => !disabled && !isLoading && inputRef.current?.click()}
        sx={{
          border: "1px dashed",
          borderColor: status === "error" ? "error.main" : "primary.main",
          borderRadius: 1,
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          bgcolor: "background.paper",
          cursor: disabled || isLoading ? "default" : "pointer",
          "&:hover": {
            bgcolor:
              disabled || isLoading ? "background.paper" : "action.hover",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          {isLoading ? (
            <>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {statusLabel}
              </Typography>
            </>
          ) : status === "success" ? (
            <>
              <CheckCircleOutlineIcon color="success" fontSize="small" />
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                {filename}
              </Typography>
            </>
          ) : (
            <>
              <UploadFileIcon color="disabled" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                No KML file selected
              </Typography>
            </>
          )}
        </Box>

        <Button
          variant="outlined"
          size="small"
          disabled={disabled || isLoading}
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
          startIcon={<UploadFileIcon />}
        >
          {status === "success" ? "Change File" : "Upload File"}
        </Button>

        <input
          ref={inputRef}
          type="file"
          accept=".kml,application/vnd.google-earth.kml+xml"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>

      {pariveshWarning && (
        <Alert
          severity="warning"
          sx={{ mt: 1 }}
          onClose={() => setPariveshWarning(false)}
        >
          Parivesh portal is not accessible. Proceeding without overlap check.
        </Alert>
      )}

      {errorMsg && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, display: "block" }}
        >
          {errorMsg}
        </Typography>
      )}

      {kmlUrl && (
        <Box
          sx={{
            mt: 2,
            maxWidth: "50%",
            border: "1px solid",
            borderColor: "success.light",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <MapComponent kmlUrl={kmlUrl} />
        </Box>
      )}

      {/* Overlap detected — no further action allowed */}
      <Dialog open={overlapOpen} onClose={() => setOverlapOpen(false)}>
        <DialogTitle>Overlap Detected</DialogTitle>
        <DialogContent>
          <Typography>
            Your KML polygon overlaps with existing Parivesh proposals. You
            cannot proceed with this land parcel. Please upload a KML file for a
            non-overlapping area.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverlapOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KmlOverlapChecker;
